import { isoDatePlusDays } from '@/lib/dates'
import { useState } from 'react'
import { Copy, Info, MonitorSmartphone, Plus, Trash2 } from 'lucide-react'
import {
  ConfirmDialog, DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useKioskMutations, useKiosks } from '../api'
import type { KioskCreated, KioskView } from '../types'

interface LocationOption { id: string; name: string }

/** Ninety days from today, in the browser's own zone, in the format the date input wants. */
function defaultExpiry(): string {
  return isoDatePlusDays(90)
}

/**
 * The tablets on the wall.
 *
 * <p>A kiosk token can record a punch and do nothing else — it grants no permissions, reaches
 * no other endpoint, and is tied to one location. It is still a credential sitting in a public
 * corridor, which is why an expiry is required rather than offered, and why the secret is shown
 * once and stored only as a hash.
 */
export function KiosksPage() {
  useBreadcrumb()
  const kiosks = useKiosks()
  const locations = useLookup<LocationOption>('locations')
  const { create, revoke } = useKioskMutations()

  const [draft, setDraft] = useState<{ name: string; locationId: string; expiresAt: string } | null>(null)
  const [issued, setIssued] = useState<KioskCreated | null>(null)
  const [toRevoke, setToRevoke] = useState<KioskView | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onCreate() {
    if (!draft) return
    setError(null)
    try {
      // Midday rather than midnight, so a token does not expire during the night shift it was
      // issued for.
      const created = await create.mutateAsync({
        name: draft.name,
        locationId: draft.locationId,
        expiresAt: new Date(`${draft.expiresAt}T12:00:00`).toISOString(),
      })
      setDraft(null)
      setIssued(created)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That kiosk could not be created')
    }
  }

  async function onRevoke() {
    if (!toRevoke) return
    setError(null)
    try {
      await revoke.mutateAsync(toRevoke.id)
      setToRevoke(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That kiosk could not be withdrawn')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Kiosks"
        subtitle="Tablets that record punches for whoever is standing at them."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ name: '', locationId: '', expiresAt: defaultExpiry() })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New kiosk
          </Button>
        }
      />

      {/* The same failure must not appear twice. While a dialog is open the reason belongs
          inside it, next to the field that caused it. */}
      {error && draft == null && (
        <Card role="alert" className="border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </Card>
      )}

      {issued && (
        <Card role="status" className="border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-medium">Copy this token into the tablet now</p>
          <p className="mt-1 break-all font-mono text-xs">{issued.token}</p>
          <p className="mt-2">{issued.note}</p>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void navigator.clipboard?.writeText(issued.token)}
            >
              <Copy className="size-4" aria-hidden="true" />
              Copy
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setIssued(null)}>
              I have it
            </Button>
          </div>
        </Card>
      )}

      <p className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        A kiosk token can record a punch at its own location and nothing else. It cannot read a
        payslip, a profile or an attendance record, however it is used.
      </p>

      <Card>
        {kiosks.isPending && <Skeleton className="m-4 h-24" />}
        {kiosks.isError && <ErrorState error={kiosks.error} onRetry={() => void kiosks.refetch()} />}
        {kiosks.isSuccess && kiosks.data.length === 0 && (
          <EmptyState
            title="No kiosks"
            description="Issue a token, put it into the tablet's app, and punches from that tablet arrive here as ordinary attendance."
          />
        )}
        {kiosks.isSuccess && kiosks.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-start text-slate-600">
                <tr>
                  <th className="p-3 font-medium">Kiosk</th>
                  <th className="p-3 font-medium">Location</th>
                  <th className="p-3 font-medium">Expires</th>
                  <th className="p-3 font-medium">Last used</th>
                  <th className="p-3 font-medium">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {kiosks.data.map((kiosk) => (
                  <tr key={kiosk.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <span className="flex items-center gap-2">
                        <MonitorSmartphone className="size-4 text-slate-500" aria-hidden="true" />
                        {kiosk.name}
                      </span>
                    </td>
                    <td className="p-3 text-slate-600">
                      {locations.data?.find((location) => location.id === kiosk.locationId)?.name
                        ?? kiosk.locationId}
                    </td>
                    <td className="p-3 text-slate-600"><DateValue value={kiosk.expiresAt} /></td>
                    <td className="p-3 text-slate-600">
                      {kiosk.lastUsedAt
                        ? <DateValue value={kiosk.lastUsedAt} />
                        : 'Never — nobody has punched at this kiosk'}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={kiosk.usable ? 'ACTIVE' : kiosk.revokedAt ? 'REVOKED' : 'EXPIRED'} />
                      {kiosk.revokedReason && (
                        <span className="block text-xs text-slate-500">{kiosk.revokedReason}</span>
                      )}
                    </td>
                    <td className="p-3 text-end">
                      {kiosk.usable && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => { setError(null); setToRevoke(kiosk) }}
                        >
                          <Trash2 className="size-4" aria-hidden="true" />
                          Withdraw
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) setDraft(null) }}
        title="New kiosk"
        description="The token is shown once, when you save."
        onSubmit={() => void onCreate()}
        submitLabel="Issue token"
        loading={create.isPending}
        error={draft ? error : null}
      >
        <div className="space-y-3">
          <div>
            <Label htmlFor="kiosk-name">Name</Label>
            <Input
              id="kiosk-name"
              value={draft?.name ?? ''}
              placeholder="Main gate"
              onChange={(event) => setDraft((current) =>
                current ? { ...current, name: event.target.value } : current)}
            />
          </div>
          <div>
            <Label htmlFor="kiosk-location">Location</Label>
            <Select
              id="kiosk-location"
              value={draft?.locationId ?? ''}
              onChange={(event) => setDraft((current) =>
                current ? { ...current, locationId: event.target.value } : current)}
            >
              <option value="">Choose a location</option>
              {locations.data?.map((location) => (
                <option key={location.id} value={location.id}>{location.name}</option>
              ))}
            </Select>
            <p className="mt-1 text-xs text-slate-500">
              Punches from this kiosk are recorded at this location, and checked against its
              geofence if one is set.
            </p>
          </div>
          <div>
            <Label htmlFor="kiosk-expiry">Expires on</Label>
            <Input
              id="kiosk-expiry"
              type="date"
              value={draft?.expiresAt ?? ''}
              onChange={(event) => setDraft((current) =>
                current ? { ...current, expiresAt: event.target.value } : current)}
            />
            <p className="mt-1 text-xs text-slate-500">
              Required, and at most 400 days out. A token on a tablet in a corridor that never
              expires is one nobody remembers to withdraw.
            </p>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={toRevoke != null}
        onOpenChange={(open) => { if (!open) setToRevoke(null) }}
        title="Withdraw this kiosk token?"
        description={`${toRevoke?.name ?? 'That kiosk'} will stop working on the next punch. Issue a new token to bring it back.`}
        confirmLabel="Withdraw"
        destructive
        onConfirm={() => void onRevoke()}
      />
    </div>
  )
}
